// src/components/GalleryManager.jsx
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import GoogleDriveImage from "./GoogleDriveImage";

/* ---------------- Notification ---------------- */
function Notification({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 24,
        right: 24,
        zIndex: 2000,
        minWidth: 220,
        padding: "12px 24px",
        borderRadius: 8,
        background: type === "success" ? "#28a745" : "#dc3545",
        color: "#fff",
        fontWeight: 600,
        boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
      }}
    >
      {message}
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          color: "#fff",
          marginLeft: 16,
          fontSize: 18,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
}

/* ---------------- Main Component ---------------- */
function GalleryManager() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  const [imageSource, setImageSource] = useState("cloudinary");
  const [imageUrl, setImageUrl] = useState("");

  const [items, setItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);

  const [notification, setNotification] = useState({
    message: "",
    type: "success",
  });

  const headingRef = useRef(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: "", type }), 2500);
  };

  const fetchItems = async () => {
    const snapshot = await getDocs(collection(db, "gallery"));
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems(data);
  };

  const saveToFirestore = async (finalImageUrl) => {
    const categoryArray = category
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

    if (editingItemId) {
      await updateDoc(doc(db, "gallery", editingItemId), {
        title,
        description,
        category: categoryArray,
        imageUrl: finalImageUrl,
        updatedAt: serverTimestamp(),
      });
      showNotification("Item updated!");
    } else {
      await addDoc(collection(db, "gallery"), {
        title,
        description,
        category: categoryArray,
        imageUrl: finalImageUrl,
        uploadedAt: serverTimestamp(),
      });
      showNotification("Item added!");
    }

    setTitle("");
    setDescription("");
    setCategory("");
    setImageUrl("");
    setEditingItemId(null);
    fetchItems();
  };

  const handleUpload = () => {
    if (!title || !description || !category) {
      showNotification("Fill all fields", "error");
      return;
    }

    // 👉 GOOGLE DRIVE (handled by component)
    if (imageSource === "url") {
      if (!imageUrl) {
        showNotification("Please provide a valid image", "error");
        return;
      }
      saveToFirestore(imageUrl);
      return;
    }

    // 👉 CLOUDINARY
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: "dgxhp09em",
        uploadPreset: "unsigned_preset",
      },
      (error, result) => {
        if (!error && result?.event === "success") {
          saveToFirestore(result.info.secure_url);
        }
        if (error) {
          showNotification("Upload failed", "error");
        }
      }
    );

    widget.open();
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "gallery", id));
    fetchItems();
    showNotification("Deleted!");
  };

  const handleEdit = (item) => {
    setEditingItemId(item.id);
    setTitle(item.title);
    setDescription(item.description);
    setCategory(item.category.join(", "));
    setImageUrl(item.imageUrl);
    setImageSource("url");

    const y =
      headingRef.current.getBoundingClientRect().top +
      window.pageYOffset -
      150;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div className="container mt-4" ref={headingRef}>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() =>
          setNotification({ message: "", type: notification.type })
        }
      />

      <h3>Gallery Manager</h3>

      <input
        className="form-control mb-2"
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <input
        className="form-control mb-2"
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <input
        className="form-control mb-2"
        placeholder="Categories (comma separated)"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      />

      <select
        className="form-select mb-3"
        value={imageSource}
        onChange={(e) => setImageSource(e.target.value)}
      >
        <option value="cloudinary">Use Cloudinary Upload</option>
        <option value="url">Use Google Drive Image</option>
      </select>

      {/* ✅ GOOGLE DRIVE COMPONENT */}
      {imageSource === "url" && (
        <GoogleDriveImage onImageReady={(url) => setImageUrl(url)} />
      )}

      <button className="btn btn-success mt-2" onClick={handleUpload}>
        {editingItemId ? "Update Item" : "Add Item"}
      </button>

      <div className="row mt-4">
        {items.map((item) => (
          <div key={item.id} className="col-md-4">
            <div className="card mb-3">
              <img
                src={item.imageUrl}
                className="card-img-top"
                style={{ height: 220, objectFit: "cover" }}
                alt=""
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/400x300?text=Image+Unavailable";
                }}
              />
              <div className="card-body">
                <h5>{item.title}</h5>
                <p>{item.description}</p>

                {item.category.map((c, i) => (
                  <span key={i} className="badge bg-secondary me-1">
                    {c}
                  </span>
                ))}

                <div className="mt-2 d-flex justify-content-between">
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GalleryManager;
